import {
  faPause,
  faPlay,
  faVolumeHigh,
  faRedo,
  faBackward,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type React from "react";
import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from "react";
import Slider from "./Slider";

interface MediaPlayerProps {
  onTimeUpdate?: (currentTime: number) => void;
  onSeek?: (time: number) => void;
  onPlayStateChange?: (isPlaying: boolean) => void;
  isLooping?: boolean;
  onToggleLoop?: () => void;
}

export interface MediaPlayerRef {
  seekToTime: (time: number) => void;
}

const MediaPlayer = forwardRef<MediaPlayerRef, MediaPlayerProps>(({ onTimeUpdate, onSeek, onPlayStateChange, isLooping = false, onToggleLoop }, ref): React.JSX.Element => {
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(100);
  const [progress, setProgress] = useState<number>(0);

  const audioRef = useRef<HTMLAudioElement>(null);

  const seekToTime = (time: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    
    audio.currentTime = time;
    onSeek?.(time);
  };

  useImperativeHandle(ref, () => ({
    seekToTime: seekToTime
  }));

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setProgress((audio.currentTime / audio.duration) * 100);
      onTimeUpdate?.(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("play", handlePlay);
    audio.addEventListener("pause", handlePause);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume / 100;
    }
  }, [volume]);


  useEffect(() => {
    // Notify parent of play state changes
    onPlayStateChange?.(isPlaying);
  }, [isPlaying, onPlayStateChange]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleEnded = (): void => {
      if (isLooping) {
        audio.currentTime = 0;
        audio.play().catch(console.error);
      }
    };

    audio.addEventListener('ended', handleEnded);
    return () => audio.removeEventListener('ended', handleEnded);
  }, [isLooping]);

  const togglePlayPause = (): void => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  const handleSeek = (seekProgress: number): void => {
    const audio = audioRef.current;
    if (!audio) return;

    const time = (seekProgress / 100) * audio.duration;
    audio.currentTime = time;
  };

  const handleVolumeChange = (newVolume: number): void => {
    setVolume(newVolume);
  };

  const formatTime = (seconds: number): string => {
    if (Number.isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <footer className="media-player">
      <div className="media-controls">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => seekToTime(0)}
              className="btn-secondary btn-icon"
              title="Rewind to beginning"
            >
              <FontAwesomeIcon icon={faBackward} />
            </button>
            
            <button
              type="button"
              onClick={togglePlayPause}
              className="btn-primary btn-icon"
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </button>
          </div>

          <div className="media-info">
            <div className="media-title">Earth, Wind & Fire - September</div>
            <div className="media-time">
              <span>{formatTime(currentTime)}</span> /{" "}
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="w-64">
            <Slider
              value={progress}
              onChange={handleSeek}
              min={0}
              max={100}
              className="w-full progress-slider"
              progressVariable="--progress-slider"
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <FontAwesomeIcon icon={faVolumeHigh} className="text-secondary w-4 h-4" />
            <Slider
              value={volume}
              onChange={handleVolumeChange}
              min={0}
              max={100}
              className="w-20 mx-2"
            />
            <button
              type="button"
              onClick={onToggleLoop}
              className={`btn-sm w-6 h-6 ${
                isLooping 
                  ? 'btn-success' 
                  : 'btn-secondary'
              }`}
              title={isLooping ? "Disable Loop" : "Enable Loop"}
            >
              <FontAwesomeIcon icon={faRedo} className={`w-3 h-3 ${isLooping ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      <audio ref={audioRef} preload="metadata">
        <source
          src="/sample/song.mp3"
          type="audio/mpeg"
        />
        Your browser does not support the audio element.
      </audio>
    </footer>
  );
});

MediaPlayer.displayName = 'MediaPlayer';

export default MediaPlayer;
